(() => {
  const POPUP_SELECTOR = '[data-orlari-newsletter-popup]';
  const DISMISSED_KEY = 'orlari_newsletter_popup_dismissed_at_v1';
  const SUBSCRIBED_KEY = 'orlari_newsletter_popup_subscribed_at_v1';
  const SUBSCRIBED_DAYS = 365;
  const PERSONALIZING_KEY = 'orlari_personalizing_session_v1';

  class OrlariNewsletterPopup {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[role="dialog"]');
      this.formState = root.dataset.formState || 'idle';
      this.delay = Math.max(0, Number(root.dataset.delaySeconds || 30)) * 1000;
      this.delayElapsed = false;
      this.scrollPercent = Math.min(100, Math.max(0, Number(root.dataset.scrollPercent || 40)));
      this.dismissDays = Math.max(1, Number(root.dataset.dismissDays || 7));
      this.exitIntent = root.dataset.exitIntent === 'true';
      this.isOpen = false;
      this.hasOpened = false;
      this.previousFocus = null;
      this.timer = null;
      this.personalizing = false;
      this.onPersonalization = this.onPersonalization.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onExitIntent = this.onExitIntent.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.initialize();
    }

    initialize() {
      this.root.querySelectorAll('[data-popup-close]').forEach((control) => {
        control.addEventListener('click', () => this.close());
      });

      const copyButton = this.root.querySelector('[data-copy-code]');
      if (copyButton) copyButton.addEventListener('click', () => this.copyCode(copyButton));

      if (this.formState === 'success') {
        this.remember(SUBSCRIBED_KEY);
        this.open(true);
        return;
      }

      if (this.formState === 'error') {
        this.open(true);
        return;
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.timer = window.setTimeout(() => this.open(true), 250);
        return;
      }

      if (this.wasRecent(SUBSCRIBED_KEY, SUBSCRIBED_DAYS) || this.wasRecent(DISMISSED_KEY, this.dismissDays)) return;

      try { this.personalizing = sessionStorage.getItem(PERSONALIZING_KEY) === '1'; } catch (error) {}
      if (this.personalizing || document.querySelector('[data-orlari-file-input]')?.files?.length) return;
      ['click', 'focusin', 'change', 'dragenter'].forEach((type) => {
        document.addEventListener(type, this.onPersonalization, true);
      });

      this.timer = window.setTimeout(() => {
        this.delayElapsed = true;
        this.open();
      }, this.delay);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.exitIntent && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.addEventListener('mouseout', this.onExitIntent);
      }
    }

    wasRecent(key, days) {
      try {
        const stored = Number(localStorage.getItem(key));
        return stored > 0 && Date.now() - stored < days * 86400000;
      } catch (error) {
        return false;
      }
    }

    onPersonalization(event) {
      if (!event.target.closest?.('[data-orlari-dropzone], [data-orlari-file-input]')) return;
      this.personalizing = true;
      try { sessionStorage.setItem(PERSONALIZING_KEY, '1'); } catch (error) {}
      this.removeTriggers();
    }

    remember(key) {
      try { localStorage.setItem(key, String(Date.now())); } catch (error) {}
    }

    onScroll() {
      const documentHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      if ((window.scrollY / documentHeight) * 100 >= this.scrollPercent) this.open();
    }

    onExitIntent(event) {
      if (!event.relatedTarget && event.clientY <= 4) this.open();
    }

    removeTriggers() {
      if (this.timer) window.clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onExitIntent);
      ['click', 'focusin', 'change', 'dragenter'].forEach((type) => {
        document.removeEventListener(type, this.onPersonalization, true);
      });
    }

    open(force = false) {
      if (this.destroyed || !this.dialog) return;
      if (this.isOpen || (this.hasOpened && !force)) return;
      if (!force && !this.delayElapsed) return;
      if (!force && (this.personalizing || document.querySelector('[data-orlari-file-input]')?.files?.length)) {
        this.removeTriggers();
        return;
      }
      if (!force && document.querySelector('cart-drawer.active')) return;
      this.hasOpened = true;
      this.isOpen = true;
      window.clearTimeout(this.hideTimer);
      this.previousFocus = document.activeElement;
      this.removeTriggers();
      this.root.hidden = false;
      document.documentElement.classList.add('orlari-popup-open');
      document.addEventListener('keydown', this.onKeydown);

      this.openFrame = window.requestAnimationFrame(() => {
        if (!this.isOpen || this.destroyed) return;
        this.root.classList.add('is-open');
        const email = this.root.querySelector('input[type="email"]');
        const target = this.formState === 'error' && email ? email : this.dialog;
        if (target) target.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      window.cancelAnimationFrame(this.openFrame);
      this.remember(DISMISSED_KEY);
      this.root.classList.remove('is-open');
      document.documentElement.classList.remove('orlari-popup-open');
      document.removeEventListener('keydown', this.onKeydown);

      this.hideTimer = window.setTimeout(() => {
        if (this.isOpen || this.destroyed) return;
        this.root.hidden = true;
        if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus();
      }, 260);
    }

    destroy() {
      this.destroyed = true;
      this.removeTriggers();
      window.clearTimeout(this.hideTimer);
      window.cancelAnimationFrame(this.openFrame);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.isOpen) document.documentElement.classList.remove('orlari-popup-open');
      this.isOpen = false;
      this.root.hidden = true;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async copyCode(button) {
      const code = this.root.querySelector('[data-discount-code]');
      const status = this.root.querySelector('[data-copy-status]');
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        button.textContent = button.dataset.copiedLabel || 'Copied';
        if (status) status.textContent = 'Discount code copied.';
      } catch (error) {
        if (status) status.textContent = `Code: ${code.textContent.trim()}`;
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(POPUP_SELECTOR).forEach((root) => {
      if (!root.orlariNewsletterPopup) root.orlariNewsletterPopup = new OrlariNewsletterPopup(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(POPUP_SELECTOR).forEach((root) => {
      root.orlariNewsletterPopup?.destroy();
      delete root.orlariNewsletterPopup;
    });
  });
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.querySelector(POPUP_SELECTOR);
    if (root && root.orlariNewsletterPopup) root.orlariNewsletterPopup.open(true);
  });
})();
